import type { UnwrapRef } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { TimeZone } from '@/shared/api/timezone';
import { TimezoneAPI } from '@/shared/api/timezone';
import { DateTimeZoneHelper } from '@/shared/legacy/lib/util/date-time-zone-helper';
import { trlMessage } from '@/shared/lib/i18n';

const TIMEZONES_KEY = 'timezones';

export function useTimezonesQuery() {
  return useQuery({
    queryKey: [TIMEZONES_KEY],
    queryFn: () => {
      return TimezoneAPI.fetchList().json();
    },
    select: ({ items }) => {
      return {
        items: items.map((timezone) => {
          const offsetFormatted = `GMT${DateTimeZoneHelper.getFormattedOffset(timezone.id)}`;
          return {
            ...timezone,
            order: DateTimeZoneHelper.getOffset(timezone.id),
            // eslint-disable-next-line no-restricted-syntax
            name: `${offsetFormatted} ${trlMessage(timezone.id)}`,
            offsetFormatted
          };
        }).sort((a, b) => a.order - b.order)
      };
    },
    initialData: {
      items: []
    },
    staleTime: Infinity,
    initialDataUpdatedAt: -Infinity
  });
}

const ORGANIZATION_TIMEZONE_KEY = 'organization-timezone';

export function useOrganizationTimezoneQuery() {
  return useQuery({
    queryKey: [ORGANIZATION_TIMEZONE_KEY],
    queryFn: () => {
      return TimezoneAPI.fetchOrganizationTimezone().json();
    },
    staleTime: Infinity,
    initialDataUpdatedAt: -Infinity
  });
}

type OrganizationTimezoneResult = UnwrapRef<ReturnType<typeof useOrganizationTimezoneQuery>['data']>;

export function useOrganizationTimezoneUpdateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['organization-timezone-update'],
    mutationFn: (timezone: TimeZone) => TimezoneAPI.updateOrganizationTimezone(timezone).json(),
    onSuccess: (timezone) => {
      queryClient.getQueriesData<OrganizationTimezoneResult>({
        queryKey: [ORGANIZATION_TIMEZONE_KEY]
      }).forEach(([key, old]) => {
        if (!old) {
          return;
        }
        queryClient.setQueryData<OrganizationTimezoneResult>(key, timezone);
      });
    }
  });
}
